import { Button, Drawer, Icon, message, Popconfirm, Tabs, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
import CommonTable, { ICommonTable } from 'src/components/common/CommonTable';
import PriceChangeTable from 'src/components/common/PriceChangeTable';
import WestPriceChangeTable from 'src/components/common/WestPriceChangeTable';
import { EnumMedicineCategory } from 'src/constants/enum';
import { EnumOrderType } from 'src/constants/Order';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import {
  IMallBrand,
  IMallCategory,
  IMallCPMDetail,
  IMallMedicineSearch,
  IMallPharmacyDetail,
  IMallTCMDetail
} from 'src/entity/MallEntity';
import { IWestApproachWay, IWestDosageForm, IWestMedicineFrequency, IWestTakeUnit } from 'src/entity/SearchEntity';
import { ISpuMedicineDetail } from 'src/entity/SpuEntity';
import { fetchMallCategoryIfNeeded } from 'src/redux/actions/mallCategoryCreator';
import { fetchMallPharmacyIfNeeded } from 'src/redux/actions/mallPharmacyCreator';
import { fetchSpuTCMIfNeeded } from 'src/redux/actions/spuTCMCreator';
import { RouterConfig } from 'src/routers/urlConfig';
import I18N from 'src/services/I18NService';
import { MallService } from 'src/services/MallService';
import * as uuid from 'uuid';
import MedicineEditForm from './MedicineEditForm';
import MedicineSearchForm from './MedicineSearchForm';
import WesternMedicineEditForm from './WesternMedicineEditForm';

const { TabPane } = Tabs;
interface IMallMedicineProps extends RouteComponentProps {
  isAgent: boolean;
  userInfo: IAgentInfo | IDrugStoreInfo;
  categoryList: IMallCategory[];
  pharmacyList: IMallPharmacyDetail[];
  tcmList: ISpuMedicineDetail[];
  cpmList: ISpuMedicineDetail[];
  dispatch: Dispatch<any>;
}

interface IMallMedicineState extends ICommonTable<IMallTCMDetail | IMallCPMDetail> {
  query: IMallMedicineSearch;
  categorySelected: IMallCategory | undefined;
  loadingBtn: boolean;
  visibleEdit: boolean;
  medicineSelected: IMallTCMDetail | IMallCPMDetail | undefined;
  selectedRowKeys: string[];
  /** 针对于西成药改价 */
  selectedRowData: IMallCPMDetail[];
  priceChangeVisible: boolean;
  ordering: string | undefined;
  /** 西成药服用单位 */
  takeUnitList: IWestTakeUnit[];
  /** 西成药剂型 */
  dosageFormList: IWestDosageForm[];
  /** 西成药给药途径 */
  approachWayList: IWestApproachWay[];
  /** 西成药用药频次 */
  medicineFrequencyList: IWestMedicineFrequency[];
  /** 品牌 */
  brandList: IMallBrand[];
}

class MallMedicineListPage extends React.Component<IMallMedicineProps, IMallMedicineState> {
  constructor(props: IMallMedicineProps) {
    super(props);
    const commonColumns = this.initCommonColumns();
    this.state = {
      rowKey: 'uuid',
      loadingTable: false,
      columns: commonColumns,
      dataSource: [],
      query: {
        offset: 0,
        limit: 10,
        is_deleted: false,
      },
      loadingBtn: false,
      categorySelected: undefined,
      visibleEdit: false,
      medicineSelected: undefined,
      selectedRowKeys: [],
      selectedRowData: [],
      priceChangeVisible: false,
      ordering: undefined,
      takeUnitList: [],
      dosageFormList: [],
      approachWayList: [],
      medicineFrequencyList: [],
      brandList: [],
    } as IMallMedicineState;
  }

  async componentDidMount() {
    await this.props.dispatch(fetchMallCategoryIfNeeded());

    if (this.props.categoryList.length > 0) {
      this.setCategoryAndSearch(this.props.categoryList[0]);

      this.props.dispatch(fetchMallPharmacyIfNeeded());
      this.props.dispatch(fetchSpuTCMIfNeeded());
    }
    await this.fetchTakeUnit();
    await this.fetchDosageForm();
    await this.fetchApproachWay();
    await this.fetchMedicineFrequency();
    await this.fetchBrand();
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  /** 获取西成药服用单位枚举 */
  async fetchTakeUnit(): Promise<void> {
    const takeUnitList = await MallService.fetchTakeUnit();
    this.setState(
      {
        takeUnitList
      },
    );
  }

  /** 获取品牌 */
  async fetchBrand(): Promise<void> {
    const res = await MallService.getAllMallBrand();
    let brandList: IMallBrand[] = [];
    if (!!res && res.results.length > 0) {
      brandList = res.results;
    }
    this.setState({
      brandList,
    })
  }

  /** 获取西成药剂型枚举 */
  async fetchDosageForm(): Promise<void> {
    const dosageFormList = await MallService.fetchDosageForm();
    this.setState(
      {
        dosageFormList
      },
    );
  }

  /** 获取西成药给药途径枚举 */
  async fetchApproachWay(): Promise<void> {
    const approachWayList = await MallService.fetchApproachWay();
    this.setState(
      {
        approachWayList
      },
    );
  }

  /**  获取西成药用药频次 */
  async fetchMedicineFrequency(): Promise<void> {
    const medicineFrequencyList = await MallService.fetchMedicineFrequency();
    this.setState(
      {
        medicineFrequencyList
      },
    );
  }

  /**
   * 初始表单头部
   * 根据中药 或 西成药 返回不同的表头结构
   */
  initCommonColumns(category?: string): Array<ColumnProps<IMallTCMDetail | IMallCPMDetail>> {
    return [
      {
        title: I18N.get('common_rowNum'),
        key: 'index',
        dataIndex: 'index',
      },
      {
        title: category && category === EnumMedicineCategory.中成药
          ? '药品全称' : I18N.get('med_short_name'),
        key: category && category === EnumMedicineCategory.中成药
          ? 'name' : 'short_name',
        dataIndex: category && category === EnumMedicineCategory.中成药
          ? 'name' : 'short_name',
      },
      {
        title: I18N.get('common_pharmacy'),
        key: 'medicine_group',
        dataIndex: 'medicine_group.name',
      },
      {
        title: category && category === EnumMedicineCategory.中成药
          ? '包装规格' : I18N.get('common_specification'),
        key: 'specification',
        dataIndex: 'specification',
        render: (text: any, record: IMallTCMDetail | IMallCPMDetail, index: number): React.ReactNode => {
          let label = '';
          if (category && category === EnumMedicineCategory.中成药) {
            label = !!record.equivalent ? `${record.equivalent}${record.retail_unit}/${record.wholesale_unit}` : '';
          } else {
            label = !!record.specification ? `${record.specification}${record.take_unit}/${record.retail_unit}` : '-/-';
            // const orderType = record.medicine_group.order_type;
            // if (orderType === EnumOrderType.处方订单) {
            //   label = !!record.specification ? `${record.specification}${record.take_unit}/${record.retail_unit}` : '';
            // } else if (orderType === EnumOrderType.药品订单) {
            //   label = !!record.specification ? `${record.specification}${record.retail_unit}/${record.wholesale_unit}` : '';
            // }
          }
          return label;
        },
      },
      {
        title: I18N.get('order_min_purchase_amount'),
        key: 'min_order_quantity',
        dataIndex: 'min_order_quantity',
      },
      {
        title: I18N.get('order_purchase_unit'),
        key: 'order_unit',
        dataIndex: 'order_unit',
      },
      {
        title: I18N.get('med_buy_price'),
        key: 'supply_price',
        dataIndex: 'supply_price',
      },
      {
        title: I18N.get('med_preserve_price'),
        key: 'floor_price',
        dataIndex: 'floor_price',
      },
      // 库存状态
      // {
      //   title: I18N.get('common_status_in_stock'),
      //   key: 'in_stock',
      //   dataIndex: 'in_stock',
      //   render: (text: any, record: IMallTCMDetail | IMallCPMDetail, index: number): React.ReactNode => {
      //     return !!record.in_stock ? I18N.get('med_in_stock') : <span style={{ color: '#F31212' }}>{I18N.get('med_out_of_stock')}</span>;
      //   },
      // },
      // 库存
      {
        title: I18N.get('common_storage'),
        key: 'storage',
        dataIndex: 'storage',
        sorter: true,
        render: (text: any, record: IMallTCMDetail | IMallCPMDetail, index: number): React.ReactNode => {
          return (
            <Tooltip
              placement="top"
              title={() => {
                return (
                  <div>
                    <div>
                      <span>{I18N.get('common_locked_storage')}：</span>
                      <span>{record.locked_storage || 0}</span>
                    </div>
                    <div>
                      <span>{I18N.get('common_available_storage')}：</span>
                      <span>{record.available_storage || 0}</span>
                    </div>
                    <div>{I18N.get('common_lock_storage_tooltip')}</div>
                  </div>
                );
              }}
            >
              <span> {record.storage} </span>
            </Tooltip>
          );
        },
      },
      // 商品状态
      {
        title: I18N.get('common_status_active'),
        key: 'is_active',
        dataIndex: 'is_active',
        render: (text: any, record: IMallTCMDetail | IMallCPMDetail, index: number): React.ReactNode => {
          return !record.is_active ? I18N.get('common_status_active_off') : <span style={{ color: '#38AD6B' }}>{I18N.get('common_status_active_on')}</span>;
        },
      },
      {
        title: I18N.get('common_operation'),
        dataIndex: 'operation',
        width: 220,
        key: 'operation',
        render: (text: any, record: IMallTCMDetail | IMallCPMDetail, index: number): React.ReactNode => {
          return (
            <div>
              <a onClick={() => this.openMedicineEdit(record)}>{I18N.get('common_edit')}</a>
              &nbsp;&nbsp;
              <Popconfirm
                onConfirm={() => this.changeActiveStatus(record, category)}
                title={`${I18N.get('common_sure')}${!record.is_active ? I18N.get('common_status_active_on') : I18N.get('common_status_active_off')}`}
                okText={I18N.get('common_ok')}
                cancelText={I18N.get('common_cancel')}
              >
                <a>{!record.is_active ? I18N.get('common_status_active_on') : I18N.get('common_status_active_off')}</a>
              </Popconfirm>
              &nbsp;&nbsp;
              {/* <Popconfirm
                onConfirm={() => this.changeInStockStatus(record)}
                title={`${I18N.get('common_sure')}${!record.in_stock ? I18N.get('med_in_stock') : I18N.get('med_out_of_stock')}`}
                okText={I18N.get('common_ok')}
                cancelText={I18N.get('common_cancel')}
              >
                <a>{!record.in_stock ? I18N.get('med_in_stock') : I18N.get('med_out_of_stock')}</a>
              </Popconfirm> */}
              {!this.props.isAgent && <a onClick={() => this.handleChangePrice(record, category)}>&nbsp;&nbsp;{`${I18N.get('med_price_change')}`}</a>}
              &nbsp;&nbsp;
              <Popconfirm
                onConfirm={() => this.changeDeleteStatus(record, category)}
                title={`${I18N.get('common_sure')}${I18N.get('common_delete')}`}
                okText={I18N.get('common_ok')}
                cancelText={I18N.get('common_cancel')}
              >
                <a>{I18N.get('common_delete')}</a>
              </Popconfirm>
            </div>
          );
        },
      },
    ];
  }

  /**
   * 生成中药/中成药TAB页
   *
   * @memberof MallMedicineListPage
   */
  tabPaneElement = (): JSX.Element[] => {
    const result: JSX.Element[] = [];
    for (const item of this.props.categoryList || []) {
      result.push(<TabPane tab={item.name} key={item.uuid} />);
    }
    return result;
  };

  /**
   * 设置类目并查询
   *
   * @memberof MallMedicineListPage
   */
  setCategoryAndSearch = (categorySelected?: IMallCategory) => {
    if (!!categorySelected) {
      const query = Object.assign({}, this.state.query, {
        category: categorySelected.code,
      });
      // 切换类目， 搜索条件要清空
      query.name = undefined;
      query.is_active = undefined;
      query.code = undefined;
      query.medicine_group = undefined;
      query.short_name = undefined;
      query.category = categorySelected.code;
      const commonColumns = this.initCommonColumns(categorySelected.code);
      this.setState(
        {
          query,
          columns: commonColumns,
          categorySelected,
          dataSource: []
        },
        () => this.handleSearch()
      );
    }

  };

  /**
   * 切换中药/中成药
   *
   * @memberof MallMedicineListPage
   */
  changeCategory = (key: string) => {
    const categorySelected = this.props.categoryList.find((t) => t.uuid === key);
    this.setCategoryAndSearch(categorySelected);
  };

  toggleOrdering(ordering: string | undefined, offset: number, limit: number) {
    this.setState({
      ordering,
    }, () => this.handleSearch(offset, limit));
  }

  /**
   * 查询
   *
   * @memberof MallMedicineListPage
   */
  handleSearch = async (offset: number = 0, limit: number = 10) => {
    const query = Object.assign({}, this.state.query, {
      limit,
      offset,
    });
    await this.doSearch(query);
  };

  /**
   * 查询
   *
   * @memberof PharmacyList
   */
  doSearch = async (query: IMallMedicineSearch) => {
    if (this.state.loadingTable) {
      return;
    }
    try {
      query.seller_user = this.props.userInfo.seller_user;
      this.setState({
        loadingTable: true,
        query,
      });

      query.ordering = this.state.ordering;
      if (query.category === EnumMedicineCategory.中药) {
        const res = await MallService.searchMedicineDetail(query);
        if (!!res) {
          this.setState({
            dataSource: res.results,
            total: res.count,
          });
        }
      } else if (query.category === EnumMedicineCategory.中成药) {
        const searchQuery = Object.assign({}, query);
        searchQuery.category = undefined;
        const res = await MallService.searchWesternMEdicineDetail(searchQuery);
        if (!!res.success) {
          this.setState({
            dataSource: res.data.results,
            total: res.data.count,
          });
        }
      }

    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingTable: false,
      });
    }
  };

  /**
   * 有货无货
   *
   * @memberof MallMedicineListPage
   */
  changeInStockStatus = async (record: IMallTCMDetail | IMallCPMDetail) => {
    if (this.state.loadingBtn) {
      return;
    }
    try {
      this.setState({
        loadingBtn: true,
      });

      if (!!record && !!record.uuid) {
        const res = await MallService.changeMedicineStock(record);
        if (!!res) {
          message.success(I18N.get('common_submit_success'));
          await this.handleSearch(this.state.query.offset, this.state.query.limit);
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingBtn: false,
      });
    }
  };

  /**
   * 上架下架
   *
   * @memberof MallMedicineListPage
   */
  changeActiveStatus = async (record: IMallTCMDetail | IMallCPMDetail, category: string | undefined) => {
    if (this.state.loadingBtn) {
      return;
    }
    const { query } = this.state;
    try {
      this.setState({
        loadingBtn: true,
        query: {
          ...query,
          category,
        }
      });

      if (!!record && !!record.uuid) {
        const res = await MallService.changeMedicineActive(record, category);
        if (!!res) {
          message.success(I18N.get('common_submit_success'));
          await this.handleSearch(this.state.query.offset, this.state.query.limit);
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingBtn: false,
        query: {
          ...query,
          category,
        }
      });
    }
  };

  /**
   * 删除药品
   *
   * @memberof MallMedicineListPage
   */
  changeDeleteStatus = async (record: IMallTCMDetail | IMallCPMDetail, category: string | undefined) => {
    if (this.state.loadingBtn) {
      return;
    }
    const { query } = this.state;

    try {
      this.setState({
        loadingBtn: true,
        query: {
          ...query,
          category,
        }
      });

      if (!!record && !!record.uuid) {
        if (record.is_active) {
          message.error(I18N.get('err_mall_delete_medicine'));
        } else {
          await MallService.deleteMedicine(record.uuid, category);
          message.success(I18N.get('common_submit_success'));
          await this.handleSearch(this.state.query.offset, this.state.query.limit);
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingBtn: false,
        query: {
          ...query,
          category,
        }
      });
    }
  };

  /**
   * 改价
   *
   * @memberof MallMedicineListPage
   */
  handleChangePrice = (record: IMallTCMDetail | IMallCPMDetail, category: string | undefined) => {
    if (!record) {
      return;
    }
    if (!category) {
      return;
    }
    if (category === EnumMedicineCategory.中成药) {
      this.setState({
        selectedRowKeys: [record.uuid],
        priceChangeVisible: true,
      });
    } else if (category === EnumMedicineCategory.中药) {
      this.setState({
        selectedRowKeys: [record.uuid],
        priceChangeVisible: true,
      });
    }

  };

  /**
   * 编辑
   *
   * @memberof MallMedicineListPage
   */
  openMedicineEdit = (record?: IMallTCMDetail | IMallCPMDetail) => {
    this.setState({
      visibleEdit: true,
      medicineSelected: record,
    });
  };

  /**
   * 上架
   * status: 上架/下架
   */
  handleBatchMedicineActive = (is_active: boolean) => {
    this.setState({
      loadingBtn: true,
    });
    MallService.batchMedicineActive(this.state.selectedRowKeys, this.state.categorySelected?.code, is_active)
      .then((res) => {
        if (res.success) {
          message.success('操作成功');
          this.handleSearch(this.state.query.offset, this.state.query.limit);
          this.setState({
            loadingBtn: false,
            selectedRowKeys: [],
            selectedRowData: [],
          });
        } else {
          message.success('操作失败');
        }
      })
      .catch(() => {
        message.success('操作失败');
      });
  };

  /**
   * 关闭编辑窗口
   *
   * @memberof MallMedicineListPage
   */
  closeMedicineEdit = () => {
    this.setState({
      visibleEdit: false,
      medicineSelected: undefined,
    });
  };

  /**
   * 编辑药品完成触发
   *
   * @memberof MallMedicineListPage
   */
  handleEditMedicine = () => {
    const query = this.state.query;
    this.setState({
      visibleEdit: false,
      medicineSelected: undefined,
      query: {
        ...query,
        category: EnumMedicineCategory.中药
      }
    });
    this.handleSearch(this.state.query.offset, this.state.query.limit);
  };

  /**
   * 编辑中药品完成触发
   *
   * @memberof MallMedicineListPage
   */
  handleEditWesterMedicine = () => {
    const query = this.state.query;
    this.setState({
      visibleEdit: false,
      medicineSelected: undefined,
      query: {
        ...query,
        category: EnumMedicineCategory.中成药
      }
    });
    this.handleSearch(this.state.query.offset, this.state.query.limit);
  };

  /**
   * 改价申请记录
   *
   * @memberof MallMedicineListPage
   */
  gotoChangePriceRecord = () => {
    this.props.history.push(`${RouterConfig.Urls.price}/change`);
  };

  /**
   * 选中项发生变化时的回调
   *
   * @memberof MallMedicineListPage
   */
  handleSelectedRow = (selectedRowKeys: string[]) => {
    this.setState({ selectedRowKeys });
  };

  /**
   * 批量改价
   *
   * @memberof MallMedicineListPage
   */
  showBatchChangePriceModel = (visible: boolean) => {
    if (!!visible) {
      this.setState({
        priceChangeVisible: visible,
      });
    } else {
      this.setState({
        selectedRowKeys: [],
        priceChangeVisible: visible,
      });
      const { categorySelected } = this.state;
      if (!!categorySelected && categorySelected.code === EnumMedicineCategory.中成药) {
        this.handleSearch(this.state.query.offset, this.state.query.limit);
      }
    }
  };

  /** 总数提示文案 */
  matchMedTotalTip(category: IMallCategory | undefined): string {
    let tip = ''
    if (category) {
      if (category.code === EnumMedicineCategory.中药) {
        tip = I18N.get('med_total_suffix')
      } else if (category.code === EnumMedicineCategory.中成药) {
        tip = '味成药';
      }
    }
    return tip;
  }

  render() {
    const {
      categorySelected,
      medicineSelected,
      visibleEdit,
      takeUnitList,
      dosageFormList,
      approachWayList,
      medicineFrequencyList,
      brandList
    } = this.state;
    const { pharmacyList, tcmList, cpmList, isAgent } = this.props;

    let spuMedicineList: ISpuMedicineDetail[] = [];
    let category: EnumMedicineCategory;
    if (!!categorySelected) {
      if (categorySelected.code === EnumMedicineCategory.中药) {
        spuMedicineList = tcmList;
        category = EnumMedicineCategory.中药;
      } else if (categorySelected.code === EnumMedicineCategory.中成药) {
        category = EnumMedicineCategory.中成药;
        spuMedicineList = cpmList;
      }
    }

    const medTotalTip = this.matchMedTotalTip(categorySelected);
    return (
      <div>
        <Tabs onChange={this.changeCategory}>{this.tabPaneElement()}</Tabs>

        {!!categorySelected && (
          <MedicineSearchForm key={categorySelected.code} category={categorySelected} pharmacyList={pharmacyList} cbSearch={this.doSearch} />
        )}

        <div className="pharmacy-content">
          <span className="pharmacy-content-desc">
            {I18N.get('common_total_prefix')}
            {this.state.total || 0}
            {medTotalTip}
          </span>
          <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
            <Button type="primary" onClick={() => this.openMedicineEdit()}>
              <Icon type="plus" />
              {I18N.get('common_new')}
              {I18N.get('med_agent')}
            </Button>
            &nbsp;&nbsp;
            <Tooltip title={I18N.get('med_price_change_batch_tips')}>
              <Popconfirm
                title={`确定将已选择的${this.state.selectedRowKeys.length}药品上架`}
                onConfirm={() => this.handleBatchMedicineActive(true)}>
                <Button disabled={this.state.selectedRowKeys.length === 0}>
                  批量上架
                </Button>
              </Popconfirm>
            </Tooltip>
            &nbsp;&nbsp;
            <Tooltip title={I18N.get('med_price_change_batch_tips')}>
              <Popconfirm
                title={`确定将已选择的${this.state.selectedRowKeys.length}药品下架`}
                onConfirm={() => this.handleBatchMedicineActive(false)}>
                <Button disabled={this.state.selectedRowKeys.length === 0}>
                  批量下架
                </Button>
              </Popconfirm>
            </Tooltip>
            {!isAgent && (
              <React.Fragment>
                &nbsp;&nbsp;
                <Tooltip title={I18N.get('med_price_change_batch_tips')}>
                  <Button type="default" disabled={this.state.selectedRowKeys.length === 0} onClick={() => this.showBatchChangePriceModel(true)}>
                    {I18N.get('med_price_change_batch')}
                  </Button>
                </Tooltip>
                &nbsp;&nbsp;
                {!!categorySelected && categorySelected.code !== EnumMedicineCategory.中成药
                  && (<Button type="default" onClick={() => this.gotoChangePriceRecord()}>
                    {I18N.get('med_price_change_record')}
                  </Button>)}
              </React.Fragment>
            )}
          </div>
        </div>

        <CommonTable
          rowKey={this.state.rowKey}
          cbToggleOrdering={(value, offset, limit) => this.toggleOrdering(value, offset, limit)}
          selectedRowKeys={this.state.selectedRowKeys}
          showPagination={(this.state.total || 0) > 0}
          columns={this.state.columns}
          loadingTable={this.state.loadingTable}
          dataSource={this.state.dataSource}
          total={this.state.total}
          offset={this.state.query.offset}
          limit={this.state.query.limit}
          callback={this.handleSearch}
          cbSelectedRow={this.handleSelectedRow}
        />

        {!!categorySelected && visibleEdit && (
          <Drawer
            title={!medicineSelected ? `${I18N.get('common_new')}${I18N.get('med_agent')}` : `${I18N.get('common_edit')}${I18N.get('med_agent')}`}
            placement="right"
            closable={true}
            onClose={this.closeMedicineEdit}
            visible={visibleEdit}
            width={800}
            maskClosable={false}
          >
            {
              categorySelected.code === EnumMedicineCategory.中药 ? (
                <MedicineEditForm
                  key={uuid.v4()}
                  isAgent={isAgent}
                  medicineInfo={medicineSelected as IMallTCMDetail | undefined}
                  category={categorySelected.code}
                  pharmacyList={pharmacyList}
                  spuMedicineList={spuMedicineList}
                  cbEdit={this.handleEditMedicine}
                />
              ) : (
                <WesternMedicineEditForm
                  key={uuid.v4()}
                  isAgent={isAgent}
                  medicineInfo={medicineSelected as IMallCPMDetail | undefined}
                  category={categorySelected.code}
                  pharmacyList={pharmacyList}
                  takeUnitList={takeUnitList}
                  dosageFormList={dosageFormList}
                  approachWayList={approachWayList}
                  medicineFrequencyList={medicineFrequencyList}
                  brandList={brandList}
                  cbEdit={this.handleEditWesterMedicine}
                />
              )
            }

          </Drawer>
        )}

        {this.state.priceChangeVisible && categorySelected && categorySelected.code === EnumMedicineCategory.中药 && (
          <PriceChangeTable
            edit={true}
            visible={this.state.priceChangeVisible}
            cbVisible={this.showBatchChangePriceModel}
            loading={false}
            selectedRowKeys={this.state.selectedRowKeys}
            userInfo={this.props.userInfo}
          />
        )}

        {this.state.priceChangeVisible && categorySelected && categorySelected.code === EnumMedicineCategory.中成药 && (
          <WestPriceChangeTable
            edit={true}
            visible={this.state.priceChangeVisible}
            cbVisible={this.showBatchChangePriceModel}
            loading={false}
            selectedRowKeys={this.state.selectedRowKeys}
            userInfo={this.props.userInfo}
            dataSource={this.state.dataSource as IMallCPMDetail[]}
          />
        )}
      </div>
    );
  }
}

export default MallMedicineListPage;



// WEBPACK FOOTER //
// ./src/components/views/Mall/MedicineList.tsx